import axios from "axios";
import { API_URLS } from "@/config/urls";

const baseURL = API_URLS.BASE_API;

export const api = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// 認証フロー関数の型定義
interface AuthFlow {
  executeAuthFlow: () => Promise<{ success: boolean; error?: string }>;
}

// 認証フローハンドラーを格納する変数
let authFlowHandler: AuthFlow | null = null;

/**
 * 認証フローハンドラーを設定
 */
export const setAuthFlowHandler = (handler: AuthFlow) => {
  authFlowHandler = handler;
};

/**
 * リクエストインターセプター（クッキーを含むリクエストを設定）
 */
api.interceptors.request.use(
  (config) => {
    // クッキーを含むようにする
    config.withCredentials = true;

    // 開発環境の場合、クッキーからトークンを取得してAuthorizationヘッダーに設定
    if (import.meta.env.DEV) {
      const token = getAuthTokenFromCookie();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// 開発環境でクッキーからトークンを取得する関数
const getAuthTokenFromCookie = (): string | null => {
  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith("humhub_auth_token=")) {
      const token = cookie.substring("humhub_auth_token=".length);
      return token;
    }
  }
  return null;
};

/**
 * レスポンスインターセプター（エンドポイント別リトライ対応）
 */
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 401エラーかつ未リトライの場合
    if (error.response?.status === 401 && !originalRequest._isRetry) {
      // OIDCログインエンドポイントでの401エラーは再試行しない
      // （OIDCトークン自体が無効なため）
      if (
        originalRequest.url?.includes(API_URLS.HUMHUB_OAUTH_LOGIN) ||
        originalRequest.url?.includes("oidc-login")
      ) {
        return Promise.reject(error);
      }

      // 認証フローハンドラーが設定されている場合
      if (authFlowHandler) {
        // リトライフラグを設定
        originalRequest._isRetry = true;

        try {
          // 認証フローを実行
          const authResult = await authFlowHandler.executeAuthFlow();

          if (authResult.success) {
            // クッキーを含むようにリクエストを設定
            originalRequest.withCredentials = true;

            // リクエストを再実行
            return api(originalRequest);
          }

          // リダイレクト系のエラーの場合は特別なエラーを投げる
          if (authResult.error?.includes("Redirecting")) {
            return Promise.reject(
              new Error("Authentication required - redirecting to login"),
            );
          }
        } catch (authError) {
          console.error(" Auth flow error:", authError);

          if (
            authError instanceof Error &&
            authError.message.includes("redirecting")
          ) {
            return Promise.reject(authError);
          }
        }
      }
    }

    return Promise.reject(error);
  },
);

export default api;
